/**
 * The arithmetic behind the targets dialog: splitting a year into months, and deriving
 * Overall from Software + Service.
 *
 * IT LIVES APART FROM THE COMPONENT because it is money arithmetic and gets assertions
 * rather than a read-through, and a module that exports both a component and its helpers
 * breaks fast refresh.
 */

export type Grid = Record<string, string>

/**
 * The dialog's working state: the boxes, and which months' Overall the dialog derived
 * rather than a person entering it.
 */
export interface Sheet {
  cells: Grid
  derived: Record<string, true>
}

export const cellKey = (category: string, monthKey: string) => `${category}::${monthKey}`

/**
 * Divide an annual figure across N months so the parts add back up to the whole.
 *
 * An even split rounded to cents does not reconcile: 100,000 / 12 is 8,333.33, and
 * twelve of those is 99,999.96. Four pence is nothing in itself, but the figure typed
 * is the figure someone will look for in the annual column, and 99,999.96 against a
 * requested 100,000 makes them doubt the number rather than the rounding. The
 * difference lands on the last month.
 */
export function spreadAcrossMonths(annual: number, count: number): number[] {
  if (count <= 0) return []
  const per = Math.round((annual / count) * 100) / 100
  const remainder = Math.round((annual - per * count) * 100) / 100
  return Array.from({ length: count }, (_, i) =>
    i === count - 1 ? Math.round((per + remainder) * 100) / 100 : per
  )
}

export const numberAt = (category: string, monthKey: string, cells: Grid) => {
  const raw = cells[cellKey(category, monthKey)]
  if (raw === undefined || String(raw).trim() === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

const withoutCell = (cells: Grid, key: string): Grid => {
  const next = { ...cells }
  delete next[key]
  return next
}

const withoutDerived = (derived: Record<string, true>, monthKey: string): Record<string, true> => {
  const next = { ...derived }
  delete next[monthKey]
  return next
}

/**
 * Which stored months' Overall may be treated as derived rather than deliberate.
 *
 * An Overall that already equals Software + Service is indistinguishable from one this
 * module would have produced. Letting it follow the parts changes nothing until a part
 * moves, and when one does, following it says what the stored figures were already saying.
 * Without this, provenance would reset on every save: entering 10,000 and 10,000, saving,
 * then correcting Software to 15,000 would leave Overall on 20,000 against parts of
 * 25,000, which is the same complaint that started this in a slower form.
 *
 * An Overall that disagrees with its parts is left alone, because that disagreement is the
 * whole reason the never-overwrite rule exists: his company Overall is 10,000,000 against
 * parts totalling 11,000,000.
 */
export function inferDerived(cells: Grid, monthKeys: string[]): Record<string, true> {
  const out: Record<string, true> = {}
  for (const monthKey of monthKeys) {
    const total = numberAt('total', monthKey, cells)
    const software = numberAt('software', monthKey, cells)
    const service = numberAt('service', monthKey, cells)
    if (total === null || software === null || service === null) continue
    if (Math.abs(total - (software + service)) < 0.01) out[monthKey] = true
  }
  return out
}

export const putCell = (
  sheet: Sheet,
  category: string,
  monthKey: string,
  value: string
): Sheet => ({
  ...sheet,
  cells: { ...sheet.cells, [cellKey(category, monthKey)]: value },
})

/**
 * Fill Overall from Software + Service, per Jason's second review.
 *
 * A figure somebody entered is never touched, because his company targets deliberately
 * disagree with their parts (Overall 10,000,000 against 11,000,000 of Software plus
 * Service) while his rep targets add up exactly. Overwriting would destroy a deliberate
 * number, and the only thing separating the two is that a person typed one of them.
 *
 * SO PROVENANCE IS TRACKED RATHER THAN INFERRED FROM EMPTINESS. The rule used to be
 * "fill Overall only while it is empty", which cannot tell this function's own output
 * from a person's entry. onChange fires per keystroke, so the first digit typed into
 * Service derived an Overall of 10,000 + 1 = 10,001; that figure then read as entered and
 * blocked every keystroke after it, so Overall froze on the first digit and the other
 * four were silently dropped. `derived` records the months this function owns, so it can
 * keep those current and still leave every other one alone.
 */
export function deriveOverall(sheet: Sheet, category: string, monthKey: string): Sheet {
  const key = cellKey('total', monthKey)

  // Touching an Overall box, including emptying it, hands that month to the person doing
  // the typing. Nothing is derived on the spot.
  //
  // AN EMPTIED OVERALL STAYS EMPTIED, because a month with no Overall target is a real
  // state: it reports as no target rather than as a shortfall, which is the whole reason
  // an empty box is not a zero. Refilling it here would have made that state unreachable
  // while both parts were set. The month goes back to following the parts the next time
  // either part changes, which is the way out of an Overall entered by mistake.
  if (category === 'total') {
    if (!sheet.derived[monthKey]) return sheet
    return { ...sheet, derived: withoutDerived(sheet.derived, monthKey) }
  }

  const software = numberAt('software', monthKey, sheet.cells)
  const service = numberAt('service', monthKey, sheet.cells)
  const mine = Boolean(sheet.derived[monthKey])

  // A sum this dialog derived, with one of its parts now cleared, is a leftover rather
  // than a target. It goes, so the month reads as no target instead of half a sum.
  if (software === null || service === null) {
    if (!mine) return sheet
    return {
      cells: withoutCell(sheet.cells, key),
      derived: withoutDerived(sheet.derived, monthKey),
    }
  }

  if (numberAt('total', monthKey, sheet.cells) !== null && !mine) return sheet

  return {
    cells: { ...sheet.cells, [key]: String(Math.round((software + service) * 100) / 100) },
    derived: { ...sheet.derived, [monthKey]: true },
  }
}
